// data.tsx

import { Stack, Theme, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
	RootCausePerimeterItemDTO,
	RootCausePerimeterValueDTO,
	RootCauseWidgetPerimeterDTO,
} from "../../../../../../datasources/api-client/data-contracts";

export type RootCausePerimeterTableRow = RootCausePerimeterItemDTO & {
	id: number;
} & Record<string, unknown>;

// Generates a unique field name for a perimeter column
export const getPerimeterField = (item: RootCausePerimeterValueDTO): string => `perimeter_${item.id ?? item.label}`;

// Flattens perimeter values into DataGrid row fields
export const mapRootCausePerimeterRows = (data: RootCauseWidgetPerimeterDTO | undefined): Record<string, unknown>[] => {
	if (!data?.items) {
		return [];
	}

	return data.items.map((item: RootCausePerimeterItemDTO) => {
		const perimeter = item.perimeter as RootCausePerimeterValueDTO[] | undefined;

		return (
			perimeter?.reduce(
				(acc: Record<string, unknown>, perimeterItem: RootCausePerimeterValueDTO) => {
					acc[getPerimeterField(perimeterItem)] = perimeterItem.value;

					return acc;
				},
				{} as Record<string, unknown>
			) ?? {}
		);
	});
};

// Creates final DataGrid rows
export const mapRootCausePerimeterTableRows = (
	data: RootCauseWidgetPerimeterDTO | undefined
): RootCausePerimeterTableRow[] => {
	const perimeterRows = mapRootCausePerimeterRows(data);

	return (
		data?.items?.map((item: RootCausePerimeterItemDTO, index: number): RootCausePerimeterTableRow => {
			return {
				...item,
				id: index,
				...perimeterRows[index],
			};
		}) ?? []
	);
};

// Displays null as "null" and undefined as "-"
const formatPerimeterCellValue = (value: unknown): React.ReactNode => {
	if (value === undefined || value === null) {
		return "-";
	}

	return String(value);
};

// Creates dynamic DataGrid columns from perimeter definitions
export const mapRootCausePerimeterColumns = (
	data: RootCauseWidgetPerimeterDTO | undefined,
	theme: Theme
): GridColDef[] => {
	const color = theme.palette.primary.contrastText;
	const columns = new Map<string, GridColDef>();

	data?.items?.forEach((item: RootCausePerimeterItemDTO) => {
		const perimeter = item.perimeter as RootCausePerimeterValueDTO[] | undefined;

		perimeter?.forEach((perimeterItem: RootCausePerimeterValueDTO) => {
			const field = getPerimeterField(perimeterItem);

			if (columns.has(field)) {
				return;
			}

			columns.set(field, {
				field,
				headerName: perimeterItem.label ?? field,
				hideable: false,
				sortable: false,
				flex: 1,
				renderHeader: () => (
					<Typography fontWeight={700} sx={{ color }} variant="body2">
						{perimeterItem.label ?? "-"}
					</Typography>
				),
				renderCell: (params: GridRenderCellParams) => (
					<Stack height="100%" justifyContent="center">
						<Typography variant="body2">{formatPerimeterCellValue(params.value)}</Typography>
					</Stack>
				),
			});
		});
	});

	return [...columns.values()];
};
